import axios from 'axios';
import React from 'react';
import { getApiErrorText } from './apiResponse';

export const RECEIVE_METHOD_PICKUP_ID = 3;

/** Типи оплати для дропдауна: «Готівкою» лише при самовивозі. */
export const filterPaymentTypesForOrder = (paymentTypesEnum, order) => {
  const isPickup = order?.userInfo?.receiveMethod?.id === RECEIVE_METHOD_PICKUP_ID;
  return (paymentTypesEnum || []).filter((pt) => {
    if (pt.databaseValue === 'CASH' && !isPickup) return false;
    return true;
  });
};

export const definePaymentStatusClassName = paymentStatus => {
  switch (paymentStatus.adminValue) {
    case 'Оплачений':
      return 'badge badge-success dropdown-toggle p-1 mt-2';
    case 'Не оплачений':
      return 'badge badge-warning dropdown-toggle p-1 mt-2';
    default:
      return 'badge dropdown-toggle mt-2';
  }
};

/** Мобільні картки замовлень: той самий вигляд без mt-2 (бейдж оплати під статусом замовлення). */
export const definePaymentStatusClassNameMobile = paymentStatus => {
  switch (paymentStatus.adminValue) {
    case 'Оплачений':
      return 'badge badge-success dropdown-toggle p-1';
    case 'Не оплачений':
      return 'badge badge-warning dropdown-toggle p-1';
    default:
      return 'badge dropdown-toggle';
  }
};

/** Порівняння сум замовлення та оплати (з допуском для float). */
const orderSumMatchesAmount = (order, amount) => {
  const sum = order.discountedPrice ?? order.price ?? 0;
  const a = Number(sum);
  const b = Number(amount);
  return a === b || Math.abs(a - b) < 0.01;
};

/**
 * Повертає копію масиву замовлень, відсортовану так, що спочатку йдуть замовлення,
 * сума яких дорівнює paymentAmount (для випадайки прив'язки оплати до замовлення).
 * @param {Array} orders - масив замовлень (об'єкти з discountedPrice/price)
 * @param {number} paymentAmount - сума оплати для порівняння
 * @returns {Array} новий відсортований масив
 */
export const sortOrdersByMatchingPaymentAmount = (orders, paymentAmount) => {
  if (!Array.isArray(orders) || orders.length === 0) return orders;
  const amount = paymentAmount ?? 0;
  return [...orders].sort((a, b) => {
    const aMatch = orderSumMatchesAmount(a, amount);
    const bMatch = orderSumMatchesAmount(b, amount);
    if (aMatch && !bMatch) return -1;
    if (!aMatch && bMatch) return 1;
    return 0;
  });
};

/** Статуси замовлення, для яких показуємо дії/тексти «як передзамовлення» (клієнту в кабінеті — один підпис). */
export const isPreorderLikeOrderStatusDb = (databaseValue) =>
  databaseValue === 'PREORDER'
  || databaseValue === 'PREORDER_DELIVERY_CONFIRMED'
  || databaseValue === 'PREORDER_AWAITING_SHIPMENT_CONFIRM';

/** Два довгі preorder-статуси: компактна іконка + короткий текст під бейджем. */
const renderPreorderAdminStatusWithIcon = (iconClass, subline) => (
  <span
    className='d-inline-flex align-items-center text-left'
    style={{ gap: '0.2rem', lineHeight: 1.1, whiteSpace: 'normal', fontSize: '0.65em', marginBottom: '2px' }}
  >
    <i
      className={`bi ${iconClass}`}
      style={{ fontSize: '0.9em', flexShrink: 0, lineHeight: 1 }}
      aria-hidden
    />
    <span style={{ color: '#111', fontWeight: 700 }}>{subline}</span>
  </span>
);

/** «Очікує підтвердження відправки» у два рядки (зручніше на вузькому екрані). */
const renderPreorderAwaitingShipmentTwoLines = () => (
  <span
    className='d-inline-flex align-items-start text-left'
    style={{ gap: '0.25rem', lineHeight: 1.15, whiteSpace: 'normal', fontSize: '0.65em', marginBottom: '2px' }}
  >
    <i
      className='bi bi-hourglass-split'
      style={{ fontSize: '0.9em', flexShrink: 0, lineHeight: 1, marginTop: '0.05em' }}
      aria-hidden
    />
    <span style={{ color: '#111', fontWeight: 700 }}>
      <span className='d-block'>очікує підтвердження</span>
      <span className='d-block'>відправки</span>
    </span>
  </span>
);

/** Основний текст у бейджі статусу (один рядок). Для всіх PREORDER-статусів завжди «Передзамовлення». */
export const getOrderStatusMainLabel = (orderStatus) => {
  if (!orderStatus) return '';
  const db = orderStatus.databaseValue;
  const admin = orderStatus.adminValue || '';
  if (isPreorderLikeOrderStatusDb(db)) {
    return 'Передзамовлення';
  }
  return admin;
};

/** Додатковий рядок під бейджем (з іконкою) тільки для довгих PREORDER-статусів. */
export const renderOrderStatusAdminLabel = (orderStatus) => {
  if (!orderStatus) return null;
  const db = orderStatus.databaseValue;
  const admin = orderStatus.adminValue || '';

  const isDeliveryConfirmed =
    db === 'PREORDER_DELIVERY_CONFIRMED'
    || (admin.includes('доставка підтверджена') && !admin.includes('очікує підтвердження'));
  const isAwaitingShipment =
    db === 'PREORDER_AWAITING_SHIPMENT_CONFIRM'
    || admin.includes('очікує підтвердження відправки');

  if (isDeliveryConfirmed) {
    return renderPreorderAdminStatusWithIcon('bi-truck', 'доставка підтверджена');
  }
  if (isAwaitingShipment) {
    return renderPreorderAwaitingShipmentTwoLines();
  }
  // Для всіх інших статусів додатковий рядок не показуємо
  return null;
};

export const defineOrderStatusClassName = orderStatus => {
  switch (orderStatus.databaseValue) {
    case 'NEW':
      return 'badge badge-info dropdown-toggle p-2';
    case 'PREORDER_AWAITING_SHIPMENT_CONFIRM':
      return 'badge badge-info dropdown-toggle p-2';
    case 'WAITING_FOR_PACKAGING':
    case 'PREORDER':
    case 'PREORDER_DELIVERY_CONFIRMED':
      return 'badge badge-success dropdown-toggle p-2';
    case 'WAITING_FOR_SENDING':
      return 'badge badge-warning dropdown-toggle p-2';
    case 'HOLD':
      return 'badge badge-primary dropdown-toggle p-2';
    case 'CANCELLED':
      return 'badge badge-danger dropdown-toggle p-2';
    case 'SENT':
      return 'badge badge-light dropdown-toggle p-2';
    case 'RETURNED':
      return 'badge badge-danger dropdown-toggle p-2';
    default:
      return '';
  }
};

export const defineProductStateClassName = productState => {
  switch (productState) {
    case 'ACTIVE':
    case 'PREORDER':
      return 'badge badge-success dropdown-toggle';
    case 'OUT_OF_STOCK':
      return 'badge badge-warning dropdown-toggle';
    case 'SOLD_OUT':
    case 'INACTIVE':
      return 'badge badge-danger dropdown-toggle';
    default:
      return '';
  }
};

export const getClassNameForProductStatus = (databaseValue) => {
  switch (databaseValue) {
    case 'IN_STOCK':
      return 'text-success';
    case 'ORDERED':
      return 'text-success';
    case 'WILL_BE_ORDERED':
      return 'text-warning';
    case 'OUT_OF_STOCK':
      return 'text-warning';
    case 'DO_NOT_DELIVERED':
      return 'text-danger';
    case 'NOT_SHIPPED':
      return 'text-danger';
    case 'RETURNED':
      return 'text-danger';
    default:
      return '';
  }
};

export const getClassNameForProductOrderType = (databaseValue) => {
  switch (databaseValue) {
    case 'PREORDER':
      return 'text-success';
    case 'TO_ORDER':
      return 'text-info';
    case 'FROM_STOCK':
      return 'text-success';
    default:
      return '';
  }
};

const formatPrice = (n) => (n ?? 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

export const renderPrice = item => {
  if (item.price === item.discountedPrice) {
    return (
      <span className='p-0 m-0 font-weight-bold'>{formatPrice(item.discountedPrice)} грн</span>
    );
  }
  return (
    <span>
      <span className='p-0 m-0 text-muted text-decoration-line-through'>{formatPrice(item.price)} грн</span>
      {' '}
      <span className='p-0 m-0 font-weight-bold'>{formatPrice(item.discountedPrice)} грн</span>
    </span>
  );
};

export const updateOrder = (orderId, price, discountedPrice, paymentStatus, orderStatus, paymentType,
                            deliveryCost, adminComment) => {
  const token = localStorage.getItem('token');
  return axios.put('/api/v1/order', {
    orderId: orderId,
    price: price,
    discountedPrice: discountedPrice,
    paymentStatus: paymentStatus,
    orderStatus: orderStatus,
    paymentType: paymentType,
    deliveryCost: deliveryCost,
    adminComment: adminComment
  }, {
    headers: {
      Authorization: token,
    },
  });
};

const handleOrderUpdate = (request, callback, onError) =>
  request.then(() => callback()).catch(error => {
    const message = getApiErrorText(error, 'Не вдалося змінити замовлення');
    if (onError) onError(message);
    else window.alert(message);
  });

export const handlePaymentStatusChange = (order, newPaymentStatus, callback, onError) =>
  handleOrderUpdate(
    updateOrder(order.orderId, null, null, newPaymentStatus.adminValue, null, null, null, null),
    callback,
    onError,
  );

export const handleOrderStatusChange = (order, newStatus, callback, onError) =>
  handleOrderUpdate(
    updateOrder(order.orderId, null, null, null, newStatus.adminValue, null, null, null),
    callback,
    onError,
  );

export const handlePaymentTypeChange = (order, newPaymentType, callback, onError) =>
  handleOrderUpdate(
    updateOrder(order.orderId, null, null, order.paymentStatus.adminValue, null, newPaymentType.adminValue, null, null),
    callback,
    onError,
  );

// Повертає YYYY-MM-DD або null для порівняння дат надходження
const getExpectedArrivalDateKey = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'string') return value.split('T')[0];
  if (Array.isArray(value) && value.length >= 3) {
    const [y, m, d] = value;
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }
  if (typeof value === 'object' && (value.year != null || value.monthValue != null || value.dayOfMonth != null)) {
    const y = value.year;
    const m = value.monthValue != null ? value.monthValue : (value.month != null ? value.month + 1 : 1);
    const d = value.dayOfMonth != null ? value.dayOfMonth : value.day;
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }
  return null;
};

const MONTHS_GENITIVE = ['січня', 'лютого', 'березня', 'квітня', 'травня', 'червня', 'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня'];
// Формат "21-го лютого" для тексту "очікується з 21-го лютого"
const formatExpectedArrivalDateOrdinal = (dateKey) => {
  if (!dateKey || typeof dateKey !== 'string') return null;
  const parts = dateKey.split('-');
  if (parts.length !== 3) return null;
  const monthNum = parseInt(parts[1], 10);
  const dayNum = parseInt(parts[2], 10);
  if (monthNum < 1 || monthNum > 12 || isNaN(dayNum)) return null;
  return `${dayNum}-го ${MONTHS_GENITIVE[monthNum - 1]}`;
};

const promoOrderText = order => order.promoCode
  ? '\nПромокод ' + order.promoCode + ': −' + formatPrice(order.promoDiscountAmount ?? 0) + ' грн'
    + '\nСума замовлення: ' + formatPrice(order.discountedPrice ?? order.finalTotal ?? order.price ?? 0) + ' грн'
  : '';

export const generateOrderText = (order) => {
  let deliveryText = '';
  if (order.userInfo.receiveMethod.id === 1) {
    deliveryText = '\nВідправка в ' + order.userInfo.city + ', ' + order.userInfo.departmentNumber + ', '
      + order.userInfo.phone + ', ' + order.userInfo.name + ' ' + order.userInfo.surname;
  } else if (order.userInfo.receiveMethod.id === 2) {
    let flatNumberText = order.userInfo.flatNumber ? ', номер квартири: ' + order.userInfo.flatNumber : '';
    deliveryText = '\nВідправка в ' + order.userInfo.city + ', ' + order.userInfo.street + ', номер будинку: '
      + order.userInfo.buildingNumber + flatNumberText + ', ' + order.userInfo.name + ' ' + order.userInfo.surname;
  }
  
  // Рахуємо передзамовлення, потім генеруємо список товарів
  const totalItems = order.orderItems?.length || 0;
  const preorderCount = order.orderItems?.filter(it => it.state?.databaseValue === 'PREORDER').length || 0;
  const isAllPreorder = totalItems > 0 && preorderCount === totalItems;

  let itemsText = '';
  if (order.orderItems && order.orderItems.length > 0) {
    order.orderItems.forEach((item, index) => {
      let itemLine = '\n' + (index + 1) + '. ' + item.name + ' - ' + item.quantity + 'шт × ' + formatPrice(item.discountedPrice) + ' грн';
      if (item.state && item.state.databaseValue && item.state.databaseValue === 'PREORDER' && !isAllPreorder) {
        itemLine += ' [В передзамовленні]';
      }
      if (item.state && item.state.databaseValue && item.state.databaseValue !== 'PREORDER' && item.parentProductName) {
        itemLine += ' [' + item.parentProductName + ']';
      }
      itemsText += itemLine;
    });
  }
  const isPartialPreorder = preorderCount > 0 && preorderCount < totalItems;

  let headerLine;
  let footerBlock;
  if (isAllPreorder) {
    headerLine = 'Вітаємо\nОтримали ваше передзамовлення';
    const preorderItems = order.orderItems?.filter(it => it.state?.databaseValue === 'PREORDER') || [];
    const uniqueDateKeys = [...new Set(preorderItems.map(it => getExpectedArrivalDateKey(it.expectedArrivalDate)).filter(Boolean))];
    const formattedDates = uniqueDateKeys.map(formatExpectedArrivalDateOrdinal).filter(Boolean);

    if (formattedDates.length <= 1) {
      // Одна дата або дати немає: очікуємо в наявності, очікується з 21-го лютого + перед відправкою сконтактуємо
      const datePhrase = formattedDates.length === 1
        ? ' з ' + formattedDates[0]
        : ' та дату надходження';
      footerBlock =
        '\n\nПідтверджуємо, очікуємо в наявності' + datePhrase + '.' +
        '\nПеред відправкою додатково сконтактуємо з вами для уточнення деталей замовлення.';
    } else {
      // Різні дати: частину в таку то, частину в таку то, бажаєте все разом чи розділити — без "перед відправкою"
      const dateList = formattedDates.join(', частину з ');
      footerBlock =
        '\n\nПідтверджуємо, частину товарів очікуємо з ' + dateList + '.' +
        '\nБажаєте отримати все разом чи розділити замовлення на 2 відправки?';
    }
  } else if (isPartialPreorder) {
    headerLine = 'Вітаємо\nОтримали ваше замовлення';
    footerBlock =
      '\n\nТип доставки: ' + order.userInfo.receiveMethod.description +
      deliveryText +
      '\nСпосіб оплати: ' + order.paymentType.clientValue +
      '\n\nЧастина товарів у передзамовленні. Бажаєте отримати все разом чи розділити замовлення на 2 відправки?';
  } else {
    headerLine = 'Вітаємо\nОтримали ваше замовлення';
    footerBlock =
      '\n\nТип доставки: ' + order.userInfo.receiveMethod.description +
      deliveryText +
      '\nСпосіб оплати: ' + order.paymentType.clientValue +
      '\nВсе вірно?';
  }

  return headerLine + itemsText + promoOrderText(order) + footerBlock;
}

export const generatePreorderText = (order) => {
  // Генеруємо список товарів
  let itemsText = '';
  if (order.orderItems && order.orderItems.length > 0) {
    order.orderItems.forEach((item, index) => {
      let itemLine = '\n' + (index + 1) + '. ' + item.name + ' - ' + item.quantity + 'шт × ' + formatPrice(item.discountedPrice) + ' грн';
      
      // Додаємо назву батьківського товару, якщо є
      if (item.state && item.state.databaseValue && item.state.databaseValue !== 'PREORDER' && item.parentProductName) {
        itemLine += ' [' + item.parentProductName + ']';
      }
      
      itemsText += itemLine;
    });
  }
  
  let deliveryText = '';
  if (order.userInfo.receiveMethod.id === 1) {
    deliveryText = '\nВідправка в ' + order.userInfo.city + ', ' + order.userInfo.departmentNumber + ', '
      + order.userInfo.phone + ', ' + order.userInfo.name + ' ' + order.userInfo.surname;
  } else if (order.userInfo.receiveMethod.id === 2) {
    let flatNumberText = order.userInfo.flatNumber ? ', номер квартири: ' + order.userInfo.flatNumber : '';
    deliveryText = '\nВідправка в ' + order.userInfo.city + ', ' + order.userInfo.street + ', номер будинку: '
      + order.userInfo.buildingNumber + flatNumberText + ', ' + order.userInfo.name + ' ' + order.userInfo.surname;
  }
  
  // Перевіряємо, чи замовлення вже оплачене
  const isPaid = order.paymentStatus && order.paymentStatus.adminValue === 'Оплачений';
  
  let text = 'Вітаємо!' +
    '\nВаше передзамовлення вже в наявності' +
    itemsText + promoOrderText(order) +
    '\n\n' + (isPaid ? 'Підкажіть, будь ласка, чи актуальні дані для доставки?' : 'Підкажіть, будь ласка, чи актуальні дані для доставки та спосіб оплати?') +
    '\n\nТип доставки: ' + order.userInfo.receiveMethod.description +
    deliveryText;
  
  // Додаємо спосіб оплати тільки якщо замовлення не оплачене
  if (!isPaid) {
    text += '\nСпосіб оплати: ' + order.paymentType.clientValue;
  }
  
  return text;
}

export const generatePreorderSoonText = (order) => {
  // Генеруємо список товарів
  let itemsText = '';
  if (order.orderItems && order.orderItems.length > 0) {
    order.orderItems.forEach((item, index) => {
      let itemLine = '\n' + (index + 1) + '. ' + item.name + ' - ' + item.quantity + 'шт × ' + formatPrice(item.discountedPrice) + ' грн';
      
      // Додаємо назву батьківського товару, якщо є
      if (item.state && item.state.databaseValue && item.state.databaseValue !== 'PREORDER' && item.parentProductName) {
        itemLine += ' [' + item.parentProductName + ']';
      }
      
      itemsText += itemLine;
    });
  }
  
  let deliveryText = '';
  if (order.userInfo.receiveMethod.id === 1) {
    deliveryText = '\nВідправка в ' + order.userInfo.city + ', ' + order.userInfo.departmentNumber + ', '
      + order.userInfo.phone + ', ' + order.userInfo.name + ' ' + order.userInfo.surname;
  } else if (order.userInfo.receiveMethod.id === 2) {
    let flatNumberText = order.userInfo.flatNumber ? ', номер квартири: ' + order.userInfo.flatNumber : '';
    deliveryText = '\nВідправка в ' + order.userInfo.city + ', ' + order.userInfo.street + ', номер будинку: '
      + order.userInfo.buildingNumber + flatNumberText + ', ' + order.userInfo.name + ' ' + order.userInfo.surname;
  }
  
  // Перевіряємо, чи замовлення вже оплачене
  const isPaid = order.paymentStatus && order.paymentStatus.adminValue === 'Оплачений';
  
  let text = 'Вітаємо!' +
    '\nВаше передзамовлення очікується в наявності протягом 1-2 днів' +
    itemsText + promoOrderText(order) +
    '\n\n' + (isPaid ? 'Підкажіть, будь ласка, чи актуальні дані для доставки?' : 'Підкажіть, будь ласка, чи актуальні дані для доставки та спосіб оплати?') +
    '\n\nТип доставки: ' + order.userInfo.receiveMethod.description +
    deliveryText;
  
  // Додаємо спосіб оплати тільки якщо замовлення не оплачене
  if (!isPaid) {
    text += '\nСпосіб оплати: ' + order.paymentType.clientValue;
  }
  
  return text;
}

export const generateDeliveryConfirmationText = (order) => {
  let deliveryText = '';
  if (order.userInfo.receiveMethod.id === 1) {
    deliveryText = 'Відправка в ' + order.userInfo.city + ', ' + order.userInfo.departmentNumber + ', '
      + order.userInfo.phone + ', ' + order.userInfo.name + ' ' + order.userInfo.surname;
  } else if (order.userInfo.receiveMethod.id === 2) {
    let flatNumberText = order.userInfo.flatNumber ? ', номер квартири: ' + order.userInfo.flatNumber : '';
    deliveryText = 'Відправка в ' + order.userInfo.city + ', ' + order.userInfo.street + ', номер будинку: '
      + order.userInfo.buildingNumber + flatNumberText + ', ' + order.userInfo.name + ' ' + order.userInfo.surname;
  }
  
  let text = 'Підкажіть, будь ласка, ці дані для відправки актуальні?' +
    '\n\n' + deliveryText;
  
  return text;
}
